/* eslint-disable react-hooks/exhaustive-deps */
import dayjs, { Dayjs } from "dayjs";
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from "react";
import { ClosedPositionsData } from "../types/papertrade";
import { useCloseOrderDelete, useCloseOrderUpdate, useFetchClosedPositions, useFetchClosedPositionStocks } from "../services/papertrade.service";
import { formatNumberWithCommas } from "../helpers/common";
import { Space, Select, Slider, DatePicker, Dropdown, Button, Modal, Drawer, message, Input } from "antd";
import { DownCircleOutlined, ExclamationCircleFilled, LeftCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useFetchStocks } from "../services/stock.service";
import { StockData } from "../types/stock";
import { useNavigate } from "react-router-dom";
import { useFetchSites } from "../services/sites.service";
import { useFetchUserAccount } from "../services/useraccount.service";
import { storage } from "../utils/storage";
import { SiteData } from "../types/sites";
import { UserAccounts } from "../types/userAccount";
import { IResponse, IResponseModel } from "../types/response";
import Images from "../assets/images";

dayjs.extend(utc);

const OrderClosedPositions = () => {
    const navigate = useNavigate();
    const { confirm } = Modal;
    const [data, setData] = useState<ClosedPositionsData[]>([]);
    const totalInvestment = useRef<string>("0.00");
    const totalProfit = useRef<string>("0.00");
    const totalProfitPercentage = useRef<string>("0");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [loading, setLoading] = useState<boolean>(true);
    const defaultScoreRange: [number, number] = [0, 1000];
    const [aiScoreRange, setAIScoreRange] = useState<[number, number]>(defaultScoreRange);
    const [aiScoreMarks, setAIScoreMarks] = useState<any>({});
    const [dateRange, setDateRange] = useState(["", ""]);
    const [stocks, setStocksList] = useState<any[]>([]);
    const [sites, setSitesList] = useState<any[]>([]);
    const [stockSymbols, setStockSymbols] = useState<string>("");
    const [siteId, setSiteId] = useState<any>(null);
    const [userAccount, setUserAccountList] = useState<any[]>([]);
    const [userAccountId, setUserAccountId] = useState<any>(null);
    const [stockSymbolOpen, setStockSymbolOpen] = useState(false);
    const isAuthenticated = useRef(false);
    const isGuestUser = useRef(false);
    const { RangePicker } = DatePicker;
    const [open, setOpen] = useState(false);
    const [orderUserAccountId, setOrderUserAccountId] = useState<any>(null);
    const [positionId, setpositionId] = useState<any>(null);
    const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
    const [closedPositionstocks, setClosedPositionStocksList] = useState<any[]>([]);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [price, setPrice] = useState<number | string>("");
    const [orderFilledAt, setOrderFilledAt] = useState<Dayjs | null>(dayjs().utc());
    const [options, setOptions] = useState<{ label: string; value: string; }[]>([]);
    const token = useRef("");
    const isSyncInProgress = useRef(false);

    const getStocksList = useFetchStocks({
        onSuccess: (response: IResponseModel<StockData>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((stock: { stockSymbol: string; }) => ({
                    label: stock.stockSymbol,
                    value: stock.stockSymbol,
                }));
                setStocksList(options);
            }
            else {
                setStocksList([]);
            }
        },
    });

    const getClosedPositionStocksList = useFetchClosedPositionStocks({
        onSuccess: (response: IResponseModel<StockData>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((stock: { stockSymbol: string; }) => ({
                    label: stock.stockSymbol,
                    value: stock.stockSymbol,
                }));
                setClosedPositionStocksList(options);
            } else {
                setClosedPositionStocksList([]);
            }
        },
    });

    const getUserAccount = useFetchUserAccount({
        onSuccess: (response: IResponseModel<UserAccounts>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((account: { userAccountName: any; userAccountId: any; }) => ({
                    label: account.userAccountName,
                    value: account.userAccountId,
                }));
                const defaultUserAccountId = options.find((x: any) => x.label === "Manual Trade")?.value;
                handleUserAccountChange(defaultUserAccountId);
                if (defaultUserAccountId) {
                    handleUserAccountChange(defaultUserAccountId);
                }
                setUserAccountList(options);
            }
            else {
                setUserAccountList([]);
            }
        },
    });

    const fetchUserAccountList = () => {
        getUserAccount.mutate({});
    }

    const getSitesList = useFetchSites({
        onSuccess: (response: IResponseModel<SiteData>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((site: { siteName: any; siteId: any; }) => ({
                    label: site.siteName,
                    value: site.siteId,
                }));
                setSitesList(options);
            } else {
                setSitesList([]);
            }
        },
    });

    const getClosedPositions = useFetchClosedPositions({
        onSuccess: (response: IResponseModel<ClosedPositionsData>) => {
            if (response.statusCode === 200 && response.data.length > 0) {
                const formattedData = response.data.map((item: any, index: { toString: () => any; }) => ({
                    ...item,
                    key: index.toString(),
                }));
                setData(formattedData);
                totalInvestment.current = (formattedData.reduce((sum: any, item: { investment: any; }) => sum + (item.investment || 0), 0)).toFixed(2);
                totalProfit.current = (formattedData.reduce((sum: any, item: { realizedEarning: any; }) => sum + (item.realizedEarning || 0), 0)).toFixed(2)
                totalProfitPercentage.current = parseFloat(totalProfit.current) > 0 ? ((parseFloat(totalProfit.current) / parseFloat(totalInvestment.current)) * 100).toFixed(2) : "0";
                setLoading(false);
            }
            else {
                setData([]);
                totalInvestment.current = "0.00";
                totalProfit.current = "0.00";
                totalProfitPercentage.current = "0.00";
                setLoading(false);
            }
            isSyncInProgress.current = false;
        },
    });

    const fetchClosedPosition = () => {
        setLoading(true);
        getClosedPositions.mutate({
            userAccountId: userAccountId ?? "",
            siteId: siteId ?? "",
            stockSymbols: stockSymbols,
            aiScoreRange: aiScoreRange.join('-'),
            positionSide: "",
            startDate: dateRange[0],
            endDate: dateRange[1],
            isGuestUser: isGuestUser.current
        });
    }

    const fetchStocksList = () => {
        getStocksList.mutate({});
    }

    const fetchClosedPositionStocksList = () => {
        getClosedPositionStocksList.mutate({});
    }

    const fetchSitesList = () => {
        getSitesList.mutate({});
    }

    const onRefresh = () => {
        fetchClosedPosition();
        onClear();
    }

    const handleDateChange = (_: any, dateStrings: any) => {
        const transformedDates = dateStrings.map((date: string, ix: number) => {
            if ((date ?? "") !== "") {
                if (ix === 0) {
                    return `${date}T00:00:00.000`;
                } else if (ix === 1) {
                    return `${date}T23:59:59.997`;
                }
            }
            return "";
        });
        setDateRange(transformedDates);
    }

    const handleSliderChange = (value: any) => {
        setAIScoreRange(value);
    };

    const handleUserAccountChange = (value: any) => {
        setUserAccountId(value);
    };

    const handleStockSymbolChange = (value: any) => {
        setStockSymbols(value?.join(','));
        if (value.length >= 1) {
            setStockSymbolOpen(false);
        }
    };

    const handleSitesChange = (value: any) => {
        setSiteId(value);
    }

    const syncAuthAndFetchData = () => {
        if (isSyncInProgress.current) {
            return;
        }
        isSyncInProgress.current = true;
        const [isAuth, isGuest] = ["isAuthenticated", "isGuestUser"].map(key =>
            JSON.parse(storage.getLocalProperty(key) ?? "false")
        );

        isAuthenticated.current = isAuth;
        isGuestUser.current = isGuest;
        token.current = (isGuest || isAuth) ? storage.getToken() : "";

        if (token.current !== "") {
            fetchStocksList();
            fetchClosedPositionStocksList();
            fetchSitesList();
            fetchUserAccountList();
            isSyncInProgress.current = false;
        } else {
            setTimeout(() => {
                isSyncInProgress.current = false;
                syncAuthAndFetchData();
            }, 100);
        }
    };

    useEffect(() => {
        if (token.current !== "" && !isSyncInProgress.current) {
            isSyncInProgress.current = true;
            fetchClosedPosition();
        } else {
            syncAuthAndFetchData();
        }
    }, [userAccountId, siteId, stockSymbols, dateRange, token.current]); // Runs only when dependencies change

    useEffect(() => {
        window.addEventListener("authStateChanged", syncAuthAndFetchData);
        return () => {
            window.removeEventListener("authStateChanged", syncAuthAndFetchData);
        };
    }, []); // Ensures event listener is added only once

    useEffect(() => {
        const [min, max] = aiScoreRange;
        const newMarks = {
            [min]: `${min}`,
            [max]: `${max}`,
        };
        setAIScoreMarks(newMarks);
    }, [aiScoreRange]);

    // Update and Delete Closed Order 

    const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^0-9.]/g, '');
        if (/^\d*(\.\d{0,2})?$/.test(value)) {
            setPrice(value);
        }
    };

    const handlePriceBlur = () => {
        if (price) {
            const formattedPrice = formatNumberWithCommas(price);
            setPrice(formattedPrice);
        }
    };

    const validateField = (field: string, value: any) => {
        if (field === "selectedSingleSymbol") {
            if (!value) {
                return "Ticker is required.";
            }
        } else if (field === "selectedOption") {
            if (!value) {
                return "Contract is required.";
            }
        } else if (field === "price") {
            if (!value) {
                return "Entry Price is required.";
            }
        } else if (field === "orderFilledAt") {
            if (!value) {
                return "Entry DateTime is required.";
            }
        }
        return "";
    };

    const showDrawer = () => {
        setOpen(true);
    };

    const onCloseDrower = () => {
        setOpen(false);
    };

    const closeOrderDelete = useCloseOrderDelete({
        onSuccess: (response: IResponse) => {
            if (response.statusCode === 200) {
                onRefresh();
            }
        }
    });

    const deleteCloseOrder = (selectedItem: any) => {
        closeOrderDelete.mutate({
            positionId: selectedItem.positionId,
        });
    };

    const onEditCloseOrder = (selectedItem: any) => {
        if (!selectedItem) {
            console.warn("No item provided.");
            return;
        }
        setOrderUserAccountId(selectedItem.userAccountId);
        setpositionId(selectedItem.positionId);
        setSelectedSymbol(selectedItem.parentStockSymbol || null);
        setSelectedOption(selectedItem.contractName || null);
        setPrice(selectedItem.closedAveragePrice ? formatNumberWithCommas(selectedItem.closedAveragePrice.toString()) : null);
        setOrderFilledAt(selectedItem.openedAt ? dayjs(selectedItem.openedAt) : null);
        setOpen(true);
    };

    const showDeleteConfirm = (selectedItem: any) => {
        confirm({
            title: 'Are you sure you want to delete this trade?',
            icon: <ExclamationCircleFilled />,
            content: (<>
                Once you delete <b>{selectedItem.contractName}</b>, this action cannot be undone.
                Please confirm if you want to proceed.
            </>),
            okText: 'Confirm',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                deleteCloseOrder(selectedItem);
            },
            onCancel() {
                console.log('Cancel');
            },
        })
    };

    const handleSubmitCloseOrderUpdate = () => {
        const selectedSymbolError = validateField('selectedSingleSymbol', selectedSymbol);
        if (selectedSymbolError) {
            message.error(selectedSymbolError);
            return;
        }

        const selectedOptionError = validateField('selectedOption', selectedOption);
        if (selectedOptionError) {
            message.error(selectedOptionError);
            return;
        }

        const priceError = validateField('price', price);
        if (priceError) {
            message.error(priceError);
            return;
        }

        const orderFilledAtError = validateField('orderFilledAt', orderFilledAt);
        if (orderFilledAtError) {
            message.error(orderFilledAtError);
            return;
        }

        const formattedDate = orderFilledAt
            ? dayjs(orderFilledAt).format("YYYY-MM-DD HH:mm:ss")
            : null;

        const param = {
            "userAccountId": orderUserAccountId ?? null,
            "positionId": positionId ?? null,
            "entryPrice": price,
            "orderFilledAt": new Date(formattedDate?.toString() ?? "")
        };
        onSubmitCloseOrderUpdate.mutate(param);
    };

    const onSubmitCloseOrderUpdate = useCloseOrderUpdate({
        onSuccess: (response: IResponse) => {
            if (response.statusCode === 200) {
                onCloseDrower();
                onRefresh();
            }
        },
    });

    const onClear = () => {
        setSelectedSymbol(null);
        setSelectedOption(null);
        setOptions([]);
        setPrice('');
        setOrderFilledAt(dayjs().utc());
    };

    const disableFutureAndToday = (current: any) => {
        if (isGuestUser.current) {
            return current && current >= dayjs().startOf('day');
        }
        return false;
    };

    return (
        <div className="relative h-full">
            <div className='p-2.5 lg:p-6 rounded-xl shadow-box'>
                <div>
                    <h2 className='text-[17px] hidden 2xl:block lg:text-[21px] font-bold text-black text-start mb-2'>Closed Positions</h2>
                </div>
                <div className="mb-2">
                    {/* Filter Button - Visible only on mobile */}
                    <div className="flex items-center gap-2 ml-7 sm:ml-0">
                        <button
                            onClick={() => setIsFilterOpen(!isFilterOpen)} // Toggle the filter section
                            className="py-1.5 px-2 lg:py-2 lg:px-4 text-black border border-blue-midnight rounded-md flex items-center justify-between gap-1.5 w-full"
                        >
                            Filter
                            {/* Conditional Icon Rendering */}
                            {isFilterOpen ? (
                                <DownCircleOutlined className="text-blue-midnight" />
                            ) : (
                                <LeftCircleOutlined className="text-blue-midnight" />
                            )}
                        </button>
                        <div>
                            <button
                                onClick={() => {
                                    onRefresh();
                                }}
                                className="py-2.5 px-2 lg:px-4 text-xs lg:text-base lg:py-2 text-white border border-blue-midnight bg-blue-midnight rounded hover:bg-transparent hover:text-blue-midnight flex items-center gap-1.5"
                            >
                                <ReloadOutlined />
                                {loading ? "Loading..." : "Refresh"}
                            </button>
                            <Drawer title="Close Order" onClose={onCloseDrower} open={open}>
                                <div>
                                    <div>
                                        <p className="font-semibold">Ticker *</p>
                                        <div className="w-full flex items-center mt-2">
                                            <Select
                                                allowClear
                                                showSearch
                                                className="w-full 2xl:min-w-80 h-10 2xl:max-w-[500px] text-start multi-user-dropdown"
                                                placeholder="Select Stock Symbol"
                                                value={selectedSymbol}
                                                options={stocks}
                                                disabled={true}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Contract *</p>
                                        <div className="w-full mt-2">
                                            <Select
                                                allowClear
                                                showSearch
                                                value={selectedOption}
                                                className="w-full 2xl:min-w-80 h-10 2xl:max-w-[500px] text-start multi-user-dropdown mb-2 md:mb-0"
                                                onChange={(value) => setSelectedOption(value)}
                                                options={options}
                                                placeholder="Select Contract Name"
                                                disabled={true}
                                                filterOption={(input, option) => {
                                                    const label = option?.label?.toString().toLowerCase().trim() ?? "";
                                                    const searchTerms = input.toLowerCase().trim().split(/\s+/);
                                                    return searchTerms.every((term) => label.includes(term));
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Exit Price *</p>
                                        <div className="w-full mt-2">
                                            <Input
                                                prefix="$"
                                                value={price}
                                                onBlur={handlePriceBlur}
                                                onChange={handlePriceChange}
                                                className="h-10"
                                                placeholder="Enter Price (e.g., 100.00)"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <p className="font-semibold">Exit DateTime *</p>
                                        <div className="w-full mt-2">
                                            <DatePicker
                                                className="w-full h-10"
                                                placeholder="Select Date"
                                                value={orderFilledAt}
                                                showTime
                                                onChange={(dateString) => {
                                                    return setOrderFilledAt(dateString);
                                                }}
                                                format="DD-MMM-YYYY HH:mm"
                                                disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end items-center mt-4">
                                    <div className="w-full">
                                        <button onClick={handleSubmitCloseOrderUpdate} className="p-2 px-2 lg:px-4 text-xs lg:text-base text-white border border-blue-midnight bg-blue-midnight rounded hover:bg-transparent hover:text-blue-midnight w-full">
                                            Update
                                        </button>
                                    </div>
                                </div>
                            </Drawer>
                        </div>
                    </div>
                    {isFilterOpen && (
                        <div className="border rounded-lg shadow-md mt-2 lg:mt-4 p-2 sm:p-3.5">
                            <div className="block lg:flex items-center justify-end">
                                <div className='w-full'>
                                    <div className="block md:flex justify-between items-center gap-3 w-full">
                                        <div className="w-full">
                                            <Select
                                                allowClear
                                                showSearch
                                                value={userAccountId}
                                                className="w-full h-10 2xl:max-w-[600px] text-start multi-user-dropdown mb-2 md:mb-0"
                                                onChange={handleUserAccountChange}
                                                options={userAccount}
                                                placeholder="Select User Account"
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>
                                        <div className="w-full">
                                            <Space direction="vertical" size={12} className='w-full mb-2 md:mb-0'>
                                                <RangePicker
                                                    onChange={handleDateChange}
                                                    className='p-2 w-full datepicker-table'
                                                    disabledDate={disableFutureAndToday}
                                                />
                                            </Space>
                                        </div>
                                        <div className="w-full">
                                            <Select
                                                allowClear
                                                showSearch
                                                value={siteId || undefined}
                                                className='w-full h-10 2xl:max-w-[600px] text-start multi-user-dropdown mb-2 md:mb-0'
                                                onChange={handleSitesChange}
                                                options={sites}
                                                placeholder="Select Site"
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>
                                        <div className="w-full">
                                            <Select
                                                mode="multiple"
                                                allowClear
                                                className='w-full 2xl:max-w-[600px] text-start multi-user-dropdown'
                                                placeholder="Select Stock Symbol" //"Select Companies"
                                                onChange={handleStockSymbolChange}
                                                options={closedPositionstocks}
                                                open={stockSymbolOpen}
                                                onDropdownVisibleChange={(visible) => setStockSymbolOpen(visible)}
                                            />
                                        </div>
                                        <div className='flex justify-between gap-4 items-center mt-2 sm:mt-0'>
                                            <div className='max-md:w-[95%]'>
                                                <p className='absolute text-sm font-semibold '>Open AI Score</p>
                                                <Slider range
                                                    value={aiScoreRange}
                                                    marks={aiScoreMarks}
                                                    min={0} max={1000}
                                                    onChange={(evt) => { handleSliderChange(evt) }}
                                                    className='w-full md:w-60 top-4 range-slider h-5' />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="overflow-auto">
                    {loading ? (
                        <div className="space-y-4">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className="animate-pulse flex w-full">
                                    <div className="w-1/12 h-6 bg-gray-300 rounded hidden lg:block"></div>
                                    <div className="w-2/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-2/12 h-6 bg-gray-300 rounded mx-1"></div>
                                </div>
                            ))}
                        </div>
                    ) :
                        data.length === 0 ? (
                            <div>
                                <p>No records found</p>
                            </div>
                        ) : (
                            <div className={`overflow-x-auto ${isFilterOpen ? 'h-[calc(100vh-445px)] md:h-[calc(100vh-315px)] xl:h-[calc(100vh-275px)]' : 'h-[calc(100vh-145px)] xl:h-[calc(100vh-165px)]'
                                }`}
                            >
                                <table className="min-w-full w-full">
                                    <thead className='sticky top-0 z-10'>
                                        <tr className='bg-[#000639] text-white text-xs lg:text-sm'>
                                            <th className="hidden lg:block py-3 mg:py-4 px-3.5 text-left whitespace-nowrap">No.</th>
                                            <th className="py-3 mg:py-4 px-2.5 lg:px-3.5 text-left whitespace-nowrap">Trade Info</th>
                                            <th className='py-3 mg:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Lot</th>
                                            <th className='py-3 mg:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>P&L</th>
                                            <th className='py-3 mg:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Entry</th>
                                            <th className='py-3 mg:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Entry Time</th>
                                            <th className='py-3 mg:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Exit</th>
                                            <th className='py-3 mg:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Exit Time</th>
                                            <th className='py-3 mg:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>AI Score</th>
                                            <th className='py-3 mg:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Invested</th>
                                            <th className='py-3 mg:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((item, index) => (
                                            <tr key={index}
                                                className={`border-b border-black ${item.gainedProfitPercentage != null
                                                    ? item.gainedProfitPercentage >= 100
                                                        ? 'bg-green-dark-green-500 text-white'
                                                        : item.gainedProfitPercentage >= 50
                                                            ? 'bg-green-light-green-200'
                                                            : item.gainedProfitPercentage <= -50
                                                                ? 'bg-red-200'
                                                                : ''
                                                    : ''
                                                    }`}>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 text-left hidden lg:block'>{index + 1}</td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-start'>
                                                    <a
                                                        href={`https://tradingview.com/chart?symbol=${item.parentStockSymbol}&interval=1D`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className='text-blue-500 hover:underline'

                                                    >
                                                        {item.parentStockSymbol}
                                                    </a>{' '}
                                                    <a
                                                        href={`https://finance.yahoo.com/quote/${item.yahooFinanceStockSymbol}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className='text-blue-500 hover:underline'

                                                    >
                                                        {item.contractName.replace(item.parentStockSymbol, '').trim()}
                                                    </a>
                                                </td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>{item.lotSize}</td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>
                                                    <span
                                                        className={`${item.realizedEarning == null
                                                            ? 'text-black'
                                                            : item.gainedProfitPercentage >= 100
                                                                ? 'text-white'
                                                                : item.realizedEarning < 0
                                                                    ? 'text-red-700'
                                                                    : 'text-green-700'
                                                            }`}
                                                    >
                                                        ${item.realizedEarning == null ? '0.00' : formatNumberWithCommas(item.realizedEarning)} ({item.realizedEarning == null ? '0' : item.gainedProfitPercentage.toFixed(2)}%)
                                                    </span>
                                                </td>
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span>
                                                        ${formatNumberWithCommas(item.openedAveragePrice)}
                                                    </span>
                                                </td>
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span>{item.openedAtAgo}</span>
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-3/4 lg:transform-translate-x-2/3 mb-2 hidden group-hover:block bg-blue-midnight text-white text-xs rounded px-2 py-1 z-10">
                                                        {new Date(item.openedAt).toLocaleString('en-GB', {
                                                            weekday: 'short',
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit',
                                                            hour12: true,
                                                        })}
                                                    </div>
                                                </td>
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span>
                                                        ${formatNumberWithCommas(item.closedAveragePrice)}
                                                    </span>
                                                </td>
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span>{item.closedAtAgo}</span>
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-3/4 lg:transform-translate-x-2/3 mb-2 hidden group-hover:block bg-blue-midnight text-white text-xs rounded px-2 py-1 z-10">
                                                        {new Date(item.closedAt).toLocaleString('en-GB', {
                                                            weekday: 'short',
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit',
                                                            hour12: true,
                                                        })}
                                                    </div>
                                                </td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>
                                                    <span>
                                                        {Math.round(item.aiScore)}
                                                    </span>
                                                </td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>${formatNumberWithCommas(item.investment)}</td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>
                                                    <Dropdown
                                                        menu={{
                                                            items: [
                                                                {
                                                                    key: '1',
                                                                    label: (
                                                                        <p onClick={() => onEditCloseOrder(item)}>Edit</p>
                                                                    ),
                                                                },
                                                                {
                                                                    key: '3',
                                                                    label: (
                                                                        <p onClick={() => showDeleteConfirm(item)} style={{ color: 'red' }} >Delete</p>
                                                                    ),
                                                                },
                                                            ],
                                                        }}
                                                        placement="bottomRight"
                                                        arrow={{ pointAtCenter: true }}
                                                    >
                                                        <Button className={`p-2.5 h-0 w-9 ${!isGuestUser.current ? 'hover:!border-blue-midnight' : ''}`}>
                                                            <img src={Images.threedots} alt="Three-dots" />
                                                        </Button>
                                                    </Dropdown>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                </div>
            </div>
            <div className="absolute bottom-0 left-0 bg-white flex justify-around items-center text-xs md:text-sm lg:text-sm w-full py-2 px-4 font-bold rounded-t-lg shadow-[0px_-2px_10px_0px_rgba(0,0,0,0.1)] border-t border-gray-200">
                <p>No. of Positions: {data.length}</p>
                <p>Total Investment: ${formatNumberWithCommas(totalInvestment.current)}</p>
                <p>Profit: ${formatNumberWithCommas(totalProfit.current)}({totalProfitPercentage.current}%)</p>
            </div>
        </div>
    );
};

export default OrderClosedPositions;